import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
  Box,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import axiosInstance from '../utils/axiosConfig';
import {
  PersonnelFormData,
  PersonnelRole,
  SkillLevel,
  EmploymentType,
  EmploymentStatus
} from '../types/Personnel';
import { useAuth } from '../contexts/AuthContext';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const PersonnelForm: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<PersonnelFormData>({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'Ireland',
    },
    fee: 0,
    roles: [], // Empty array, but we'll validate before submission
    employmentStatus: EmploymentStatus.External,
    skillLevel: SkillLevel.Beginner,
    experience: 0,
    performances: [],
    availability: {
      from: new Date(),
      to: new Date(),
    },
    specialization: '',
    equipment: [],
    certifications: [],
    bio: '',
    profileImage: '',
    socialMedia: {
      linkedin: '',
      twitter: '',
      instagram: '',
    },
    // ⭐ NEW: Staff employment fields
    employmentType: EmploymentType.Contractor,
    startDate: new Date()
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ⭐ NEW: Check if current user is editing themselves
  const isEditingSelf = false; // Temporarily hardcode to false
  const isStaff = formData.roles.includes(PersonnelRole.Staff);
  const isAdmin = user?.role === 'admin' || user?.role === 'gm' || user?.role === 'team';

  // Determine if we show advanced fields
  const showAdvancedFields = formData.roles.some(
    (role) => role !== PersonnelRole.Staff && role !== PersonnelRole.Volunteer
  );

  useEffect(() => {
    if (id) {
      const fetchPerson = async () => {
        try {
          const response = await axiosInstance.get<PersonnelFormData>(`/api/personnel/${id}`);
          let data = response.data;

          // Ensure 'roles' is always an array:
          if (!Array.isArray(data.roles)) {
            if (typeof data.roles === 'string') {
              data.roles = [data.roles as PersonnelRole];
            } else if (!data.roles) {
              data.roles = [];
            }
          }

          // Ensure dates are properly formatted
          if (data.availability) {
            if (data.availability.from) {
              data.availability.from = new Date(data.availability.from);
            }
            if (data.availability.to) {
              data.availability.to = new Date(data.availability.to);
            }
          }

          // ⭐ NEW: Handle start date
          if (data.startDate) {
            data.startDate = new Date(data.startDate);
          }

          // Ensure fee is a number
          if (typeof data.fee !== 'number') {
            data.fee = parseFloat(data.fee as any) || 0;
          }

          // Ensure experience is a number
          if (typeof data.experience !== 'number') {
            data.experience = parseInt(data.experience as any) || 0;
          }

          // Ensure address object exists
          if (!data.address) {
            data.address = {
              street: '',
              city: '',
              state: '',
              postalCode: '',
              country: 'Ireland',
            };
          }

          setFormData(data);
        } catch (error) {
          console.error('Error fetching personnel data:', error);
          setError('Failed to load personnel data. Please try again.');
        }
      };
      fetchPerson();
    }
  }, [id]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Check required fields
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.roles.length) errors.roles = 'At least one role is required';
    if (formData.fee < 0) errors.fee = 'Fee cannot be negative';
    if (formData.experience < 0) errors.experience = 'Experience cannot be negative';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    console.log('Field changed:', name, 'New value:', value);


    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    // Handle numeric fields
    if (name === 'fee' || name === 'experience') {
      const numValue = parseFloat(value);
      setFormData((prev) => ({ ...prev, [name]: isNaN(numValue) ? 0 : numValue }));
      return;
    }

    // For other fields
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleRoleChange = (e: SelectChangeEvent<string[]>) => {
    const selectedValues = e.target.value as string[];
    const roles = selectedValues.map((val) => val as PersonnelRole);
    
    // Clear error when user selects roles
    if (formErrors.roles) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated.roles;
        return updated;
      });
    }
    
    setFormData((prev) => ({
      ...prev,
      roles: roles,
    }));
  };

  const handleArrayChange = (name: keyof PersonnelFormData, value: string) => {
    const arrayValue = value.split(',').map((item) => item.trim()).filter(Boolean);
    setFormData((prev) => ({
      ...prev,
      [name]: arrayValue,
    }));
  };

  // ⭐ NEW: Handle annual leave adjustment (admin only)
  const handleAnnualLeaveAdjustment = async (newAmount: number, reason: string) => {
    if (!id || !isAdmin || isEditingSelf) return;

    try {
      await axiosInstance.patch(`/api/personnel/${id}/leave/adjust-annual`, {
        annualLeaveTotal: newAmount,
        reason: reason || 'Admin adjustment'
      });

      setSuccess('Annual leave entitlement updated successfully');
      
      // Refresh the data
      window.location.reload();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update annual leave');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fix the errors in the form before submitting.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      // Prepare data for submission
      const dataToSubmit = {
        ...formData,
        fee: typeof formData.fee === 'string' ? parseFloat(formData.fee) : formData.fee || 0,
        experience: typeof formData.experience === 'string' ? parseInt(formData.experience) : formData.experience || 0
      };

      if (id) {
        await axiosInstance.patch(`/api/personnel/${id}`, dataToSubmit);
        setSuccess('Personnel updated successfully!');
        setTimeout(() => navigate('/admin/personnel'), 1500);
      } else {
        await axiosInstance.post(`/api/personnel`, dataToSubmit);
        setSuccess('Personnel created successfully!');
        setTimeout(() => navigate('/admin/personnel'), 1500);
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      const message = error.response?.data?.message || 'Error submitting the form. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        {id ? 'Edit Personnel' : 'Create New Personnel'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              error={!!formErrors.phone}
              helperText={formErrors.phone}
            />
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Address (Optional - for invoicing purposes)
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={formData.address?.street || ''}
              onChange={(e) => handleAddressChange('street', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={formData.address?.city || ''}
              onChange={(e) => handleAddressChange('city', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State/County"
              value={formData.address?.state || ''}
              onChange={(e) => handleAddressChange('state', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.address?.postalCode || ''}
              onChange={(e) => handleAddressChange('postalCode', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              value={formData.address?.country || 'Ireland'}
              onChange={(e) => handleAddressChange('country', e.target.value)}
            />
          </Grid>

          {/* Roles (multiple) */}
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.roles}>
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                name="roles"
                label="Roles"
                value={formData.roles}
                onChange={handleRoleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={MenuProps}
                required
              >
                {Object.values(PersonnelRole).map((roleOption) => (
                  <MenuItem key={roleOption} value={roleOption}>
                    <Checkbox checked={formData.roles.indexOf(roleOption) > -1} />
                    <ListItemText primary={roleOption} />
                  </MenuItem>
                ))}
              </Select>
              {formErrors.roles && (
                <FormHelperText error>{formErrors.roles}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          {/* Employment Status */}
<Grid item xs={12} sm={6}>
  <FormControl fullWidth required>
    <InputLabel>Employment Status</InputLabel>
    <Select
      name="employmentStatus"
      label="Employment Status"
      value={formData.employmentStatus}
      onChange={handleChange}
      
    >
      {Object.values(EmploymentStatus).map((status) => (
        <MenuItem key={status} value={status}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Grid>

          {/* ⭐ NEW: Staff Leave Management Section */}
          {isStaff && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Staff Employment & Leave Management
                </Typography>
              </Grid>

              {/* Employment Type */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    name="employmentType"
                    label="Employment Type"
                    value={formData.employmentType || EmploymentType.Contractor}
                    onChange={handleChange}
                    disabled={isEditingSelf && !isAdmin}
                  >
                    {Object.values(EmploymentType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </MenuItem>
                    ))}
                  </Select>
                  {isEditingSelf && (
                    <FormHelperText>
                      Cannot edit your own employment details. Ask another admin.
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Employment Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    formData.startDate
                      ? new Date(formData.startDate).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      startDate: e.target.value ? new Date(e.target.value) : new Date(),
                    }))
                  }
                  disabled={isEditingSelf && !isAdmin}
                  helperText={isEditingSelf ? "Cannot edit your own start date" : ""}
                />
              </Grid>

              {/* Leave Entitlement Display */}
              {formData.leaveEntitlement && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Leave Entitlement ({formData.leaveEntitlement.annualLeaveYear})
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Annual Leave */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Annual Leave Entitlement (Days)"
                          type="number"
                          value={formData.leaveEntitlement.annualLeaveTotal || 20}
                          InputProps={{ 
                            readOnly: isEditingSelf || !isAdmin,
                            inputProps: { min: 0, max: 50 }
                          }}
                          helperText={
                            isEditingSelf 
                              ? "Cannot edit your own leave entitlement" 
                              : isAdmin 
                                ? "Admin can adjust this value"
                                : "Read only"
                          }
                          onChange={(e) => {
                            if (!isEditingSelf && isAdmin) {
                              const newValue = parseInt(e.target.value);
                              setFormData(prev => ({
                                ...prev,
                                leaveEntitlement: {
                                  ...prev.leaveEntitlement!,
                                  annualLeaveTotal: newValue
                                }
                              }));
                            }
                          }}
                        />
                      </Grid>

                      {/* Current Balances (Read Only) */}
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Current Balances:
                          </Typography>
                          <Typography variant="body2">
                            AL Used: {formData.leaveEntitlement.annualLeaveUsed || 0} days
                          </Typography>
                          <Typography variant="body2">
                            AL Remaining: {(formData.leaveEntitlement.annualLeaveTotal || 20) - (formData.leaveEntitlement.annualLeaveUsed || 0)} days
                          </Typography>
                          <Typography variant="body2">
                            TIL Hours: {formData.leaveEntitlement.timeInLieuHours || 0} hours
                          </Typography>
                          {formData.leaveEntitlement.timeInLieuHours > 16 && (
                            <Typography variant="body2" color="warning.main">
                              ⚠️ TIL exceeds 16-hour guideline
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {formData.leaveEntitlement.lastModified && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Last modified: {new Date(formData.leaveEntitlement.lastModified).toLocaleDateString()}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              )}
            </>
          )}

          {/* Skill Level */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Skill Level</InputLabel>
              <Select
                name="skillLevel"
                label="Skill Level"
                value={formData.skillLevel}
                onChange={handleChange}
                required
              >
                {Object.values(SkillLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fee */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Fee"
              name="fee"
              type="number"
              value={formData.fee}
              onChange={handleChange}
              required
              error={!!formErrors.fee}
              helperText={formErrors.fee}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          {/* Experience */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Experience (years)"
              name="experience"
              type="number"
              value={formData.experience}
              onChange={handleChange}
              required
              error={!!formErrors.experience}
              helperText={formErrors.experience}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>

          {/* Specialization */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Specialization"
              name="specialization"
              value={formData.specialization || ''}
              onChange={handleChange}
            />
          </Grid>

          {/* Advanced Fields (existing logic) */}
          {(showAdvancedFields || (formData.equipment && formData.equipment.length > 0) || (formData.certifications && formData.certifications.length > 0) || formData.bio) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Additional Details
                </Typography>
              </Grid>

              {/* Equipment */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Equipment (comma separated)"
                  name="equipment"
                  value={formData.equipment?.join(', ') || ''}
                  onChange={(e) => handleArrayChange('equipment', e.target.value)}
                  placeholder="e.g. 'Mixer, Microphone, Cables'"
                />
              </Grid>

              {/* Certifications */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certifications (comma separated)"
                  name="certifications"
                  value={formData.certifications?.join(', ') || ''}
                  onChange={(e) => handleArrayChange('certifications', e.target.value)}
                  placeholder="e.g. 'OSHA, Rigging, Safety'"
                />
              </Grid>

              {/* Availability */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Available From"
                  InputLabelProps={{ shrink: true }}
                  value={
                    formData.availability?.from
                      ? new Date(formData.availability.from).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      availability: {
                        ...prev.availability,
                        from: e.target.value ? new Date(e.target.value) : new Date(),
                      },
                    }))
                  }
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Available To"
                  InputLabelProps={{ shrink: true }}
                  value={
                    formData.availability?.to
                      ? new Date(formData.availability.to).toISOString().split('T')[0]
                      : ''
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      availability: {
                        ...prev.availability,
                        to: e.target.value ? new Date(e.target.value) : new Date(),
                      },
                    }))
                  }
                />
              </Grid>

              {/* Bio */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Bio"
                  name="bio"
                  value={formData.bio || ''}
                  onChange={handleChange}
                />
              </Grid>

              {/* Profile Image */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Profile Image URL"
                  name="profileImage"
                  value={formData.profileImage || ''}
                  onChange={handleChange}
                />
              </Grid>

              {/* Social Media */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Social Media Links
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="LinkedIn URL"
                  value={formData.socialMedia?.linkedin || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      socialMedia: {
                        ...prev.socialMedia,
                        linkedin: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Twitter URL"
                  value={formData.socialMedia?.twitter || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      socialMedia: {
                        ...prev.socialMedia,
                        twitter: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Instagram URL"
                  value={formData.socialMedia?.instagram || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      socialMedia: {
                        ...prev.socialMedia,
                        instagram: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </>
          )}

          {/* Submit Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/admin/personnel')}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? 'Saving...' 
                  : id 
                    ? 'Update Personnel' 
                    : 'Create Personnel'
                }
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Paper>
  );
};

export default PersonnelForm;